var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Alignment, Collider, CollisionComponent, Component, PhysXComponent, Shape, ProjectionType, TextEffect, VerticalAlignment, } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import { mat4, vec3 } from 'gl-matrix';
import { TextComponent, TextWrapMode } from '@wonderlandengine/api';
import { propsEqual } from './helpers/props-helpers.js';
import { COLLIDER_THICKNESS, debug, DEBUG_EVENTS, DEFAULT_FONT_SIZE, TEXT_BASE_SIZE, } from './constants.js';
/* These are used by props */
export { Align, Display, FlexDirection, Justify, Overflow, PositionType, Wrap, } from 'yoga-layout/load';
/* These are used by the renderer only */
import { MeasureMode, Align, Display, Edge, Direction, FlexDirection, Gutter, Justify, Overflow, PositionType, Wrap, loadYoga, } from 'yoga-layout/load';
import { CursorTarget } from '@wonderlandengine/components';
import Reconciler from 'react-reconciler';
import { buildNineSliceProps, buildRoundedRectangleMeshProps, } from './helpers/mesh-helpers.js';
import { roundedRectangle, roundedRectangleOutline, } from './components/rounded-rectangle-mesh.js';
import { computeDimensionsFromBoundingBox, computeTextScaleAndWrap, } from './helpers/text-helpers.js';
import { nineSlice } from './components/nine-slice.js';
import { computeMeshChildTransforms } from './helpers/mesh-transform-helpers.js';
import { setPositionCenter, setPositionRight, setPositionLeft, } from './helpers/position-helpers.js';
export var UISpace;
(function (UISpace) {
    UISpace[UISpace["World"] = 0] = "World";
    UISpace[UISpace["Screen"] = 1] = "Screen";
})(UISpace || (UISpace = {}));
export var ScalingType;
(function (ScalingType) {
    /**
     * The sizes absolute to the screen size. A pixel in the UI is a pixel on screen.
     * This is the default.
     */
    ScalingType[ScalingType["Absolute"] = 0] = "Absolute";
    /**
     * The height of the UI will be fixed to the value set in the `manualHeight` property.
     */
    ScalingType[ScalingType["FixedHeight"] = 1] = "FixedHeight";
    /**
     * The height of the UI will be fixed to the value set in the `manualHeight` property.
     * But if the width is below a certain threshold, the height will be scaled down anyway
     */
    ScalingType[ScalingType["FixedHeightLimitedWidth"] = 2] = "FixedHeightLimitedWidth";
})(ScalingType || (ScalingType = {}));
const tempPos = [0, 0, 0];
const tempScale = [0, 0, 0];
const invProj = new Float32Array(16);
const topLeft = vec3.create();
const bottomRight = vec3.create();
const tempBBVec4 = new Float32Array(4);
var RayCastMode;
(function (RayCastMode) {
    RayCastMode[RayCastMode["Auto"] = 0] = "Auto";
    RayCastMode[RayCastMode["AlwaysCollider"] = 1] = "AlwaysCollider";
    RayCastMode[RayCastMode["AlwaysPhysX"] = 2] = "AlwaysPhysX";
})(RayCastMode || (RayCastMode = {}));
export function computeTextDimensions(n, context) {
    const o = n.object;
    const t = o.getComponent(TextComponent);
    const { scale, wrapWidth } = computeTextScaleAndWrap(n.node.getComputedWidth(), n.props.fontSize, context.comp.scaling, TEXT_BASE_SIZE, DEFAULT_FONT_SIZE);
    o.setScalingLocal([scale, scale, scale]);
    if (!isNaN(wrapWidth)) {
        t.wrapWidth = wrapWidth;
        t.getBoundingBoxForText(n.props.text, tempBBVec4);
        const { width, height } = computeDimensionsFromBoundingBox(tempBBVec4, scale, context.comp.scaling);
        return { width, height };
    }
    return { width: 0, height: 0 };
}
export function applyLayoutToSceneGraph(n, context, force) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    // replicate debug guard from original file (renderer.ts) if needed
    if (!force && !n.dirty && !n.node.hasNewLayout())
        return;
    n.node.markLayoutSeen();
    if ((_a = n.object) === null || _a === void 0 ? void 0 : _a.isDestroyed)
        n.object = null;
    const o = (_b = n.object) !== null && _b !== void 0 ? _b : (() => {
        var _a, _b, _c, _d;
        if ((_b = (_a = n.parent) === null || _a === void 0 ? void 0 : _a.object) === null || _b === void 0 ? void 0 : _b.isDestroyed)
            n.parent.object = null;
        const o = context.comp.engine.scene.addObject((_d = (_c = n.parent) === null || _c === void 0 ? void 0 : _c.object) !== null && _d !== void 0 ? _d : context.comp.object);
        return o;
    })();
    n.object = o;
    o.parent = (_d = (_c = n.parent) === null || _c === void 0 ? void 0 : _c.object) !== null && _d !== void 0 ? _d : (_e = context === null || context === void 0 ? void 0 : context.comp) === null || _e === void 0 ? void 0 : _e.object;
    o.resetPositionRotation();
    o.resetScaling();
    if (n.tag === 'text3d') {
        applyTextLayout(n, o, context);
    }
    else {
        /* mesh and everything else */
        if (context && context.comp && context.comp.scaling) {
            setPositionLeft(o, n, context.comp.scaling);
        }
    }
    if (n.tag === 'mesh' || n.tag === 'roundedRectangle' || n.tag === 'nineSlice') {
        const child = (_f = o.findByNameDirect('mesh')[0]) !== null && _f !== void 0 ? _f : (() => {
            const child = context.comp.engine.scene.addObject(o);
            child.name = 'mesh';
            return child;
        })();
        let sw = n.node.getComputedWidth() * context.comp.scaling[0];
        let sh = n.node.getComputedHeight() * context.comp.scaling[1];
        if (isNaN(sw) || isNaN(sh)) {
            sw = 0;
            sh = 0;
        }
        const transforms = computeMeshChildTransforms(sw, sh, n.props.z, n.tag);
        const m = (_g = child.getComponent('mesh')) !== null && _g !== void 0 ? _g : child.addComponent('mesh', {});
        m.material = n.props.material;
        let mesh = m.mesh;
        if (n.tag === 'roundedRectangle') {
            const p = buildRoundedRectangleMeshProps(n.props, sw, sh, context.comp.scaling);
            const props = (_h = m.roundedRectangleProps) !== null && _h !== void 0 ? _h : {};
            const needsUpdate = !propsEqual(props, p);
            const borderSize = ((_j = n.props.borderSize) !== null && _j !== void 0 ? _j : 0) * context.comp.scaling[0];
            if (needsUpdate) {
                mesh = roundedRectangle(context.comp.engine, p.sw, p.sh, p.rounding, p.resolution, {
                    tl: p.tl,
                    tr: p.tr,
                    bl: p.bl,
                    br: p.br,
                }, mesh);
                m.roundedRectangleProps = p;
            }
            const oldBorderSize = (_k = m.borderSize) !== null && _k !== void 0 ? _k : 0;
            const needsBorderMeshUpdate = oldBorderSize != borderSize;
            const bm = (_l = child.getComponent('mesh', 1)) !== null && _l !== void 0 ? _l : child.addComponent('mesh', {});
            bm.active = borderSize != 0;
            bm.material = n.props.borderMaterial;
            if (needsBorderMeshUpdate) {
                if (borderSize != 0) {
                    bm.mesh = roundedRectangleOutline(context.comp.engine, p.sw, p.sh, p.rounding, p.resolution, borderSize, {
                        tl: p.tl,
                        tr: p.tr,
                        bl: p.bl,
                        br: p.br,
                    }, bm.mesh);
                    m.borderSize = borderSize;
                }
            }
            child.setPositionLocal(transforms.position);
            if (transforms.resetScaling)
                child.resetScaling();
        }
        else if (n.tag === 'nineSlice') {
            const p = buildNineSliceProps(n.props, sw, sh, context.comp.scaling);
            const props = (_m = m.nineSliceProps) !== null && _m !== void 0 ? _m : {};
            const needsUpdate = !propsEqual(props, p);
            if (needsUpdate) {
                mesh = nineSlice(context.comp.engine, p.sw, p.sh, p.borderSize, p.borderTextureSize, mesh);
                m.nineSliceProps = p;
            }
            child.setPositionLocal(transforms.position);
            if (transforms.resetScaling)
                child.resetScaling();
        }
        else {
            child.setPositionLocal(transforms.position);
            if (transforms.scaling)
                child.setScalingLocal(transforms.scaling);
        }
        m.mesh = (_o = n.props.mesh) !== null && _o !== void 0 ? _o : mesh;
    }
    (_p = n.children) === null || _p === void 0 ? void 0 : _p.forEach((c) => {
        applyLayoutToSceneGraph(c, context, force);
        if (c.object && !c.object.isDestroyed) {
            c.object.parent = n.object;
        }
    });
}
export function applyTextLayout(nodeWrapper, object, context) {
    var _a, _b, _c;
    const align = nodeWrapper.props.textAlign;
    let alignment = Alignment.Left;
    if (align === 'center') {
        setPositionCenter(object, nodeWrapper, context.comp.scaling);
        alignment = Alignment.Center;
    }
    else if (align === 'right') {
        setPositionRight(object, nodeWrapper, context.comp.scaling);
        alignment = Alignment.Right;
    }
    else {
        setPositionLeft(object, nodeWrapper, context.comp.scaling);
    }
    const wrap = nodeWrapper.props.textWrap;
    let textWrapMode = TextWrapMode.None;
    if (wrap === 'soft') {
        textWrapMode = TextWrapMode.Soft;
    }
    else if (wrap === 'hard') {
        textWrapMode = TextWrapMode.Hard;
    }
    else if (wrap === 'clip') {
        textWrapMode = TextWrapMode.Clip;
    }
    let textEffect = TextEffect.None;
    switch (nodeWrapper.props.textEffect) {
        case 'outline':
            textEffect = TextEffect.Outline;
            break;
        case 'shadow':
            textEffect = TextEffect.Shadow;
            break;
        default:
            textEffect = TextEffect.None;
            break;
    }
    let textComponent = object.getComponent(TextComponent);
    if (!textComponent) {
        textComponent = object.addComponent(TextComponent, {
            text: nodeWrapper.props.text,
            alignment,
            effect: textEffect,
            effectOffset: nodeWrapper.props.textEffectOffset,
            verticalAlignment: VerticalAlignment.Top,
            wrapMode: textWrapMode,
            material: (_a = nodeWrapper.props.material) !== null && _a !== void 0 ? _a : context.comp.textMaterial,
        });
    }
    const scale = TEXT_BASE_SIZE *
        ((_b = nodeWrapper.props.fontSize) !== null && _b !== void 0 ? _b : DEFAULT_FONT_SIZE) *
        context.comp.scaling[1];
    object.setScalingLocal([scale, scale, scale]);
    textComponent.material = (_c = nodeWrapper.props.material) !== null && _c !== void 0 ? _c : context.comp.textMaterial;
    const adjustedWidth = (nodeWrapper.node.getComputedWidth() * context.comp.scaling[0]) / scale;
    if (!isNaN(adjustedWidth)) {
        textComponent.wrapWidth = adjustedWidth;
    }
}
export function applyToYogaNode(tag, node, props, wrapper, ctx) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    if (tag === 'text3d') {
        const p = props;
        let t = (_a = wrapper.object) === null || _a === void 0 ? void 0 : _a.getComponent(TextComponent);
        if (!t) {
            wrapper.props.text = p.text;
            wrapper.props.textAlign = p.textAlign;
            wrapper.props.textWrap = p.textWrap;
            wrapper.props.material = p.material;
            wrapper.props.textEffect = p.textEffect;
            wrapper.props.textEffectOffset = p.textEffectOffset;
            wrapper.props.textEffectColor = p.textEffectColor;
            applyLayoutToSceneGraph(wrapper, ctx, true);
            t = (_b = wrapper.object) === null || _b === void 0 ? void 0 : _b.getComponent(TextComponent);
        }
        if (t.text !== p.text) {
            t.text = p.text;
            node.markDirty();
        }
        node.setWidth(props.width);
        node.setHeight(props.height);
    }
    else {
        if (ctx) {
            applyLayoutToSceneGraph(wrapper, ctx, true);
        }
        node.setWidth(props.width);
        node.setHeight(props.height);
    }
    if (wrapper.props.alignContent !== props.alignContent)
        node.setAlignContent((_c = props.alignContent) !== null && _c !== void 0 ? _c : Align.FlexStart);
    if (wrapper.props.alignItems !== props.alignItems)
        node.setAlignItems((_d = props.alignItems) !== null && _d !== void 0 ? _d : Align.Stretch);
    if (wrapper.props.alignSelf !== props.alignSelf)
        node.setAlignSelf((_e = props.alignSelf) !== null && _e !== void 0 ? _e : Align.FlexStart);
    if (wrapper.props.aspectRatio !== props.aspectRatio)
        node.setAspectRatio(props.aspectRatio);
    if (wrapper.props.display !== props.display)
        node.setDisplay((_f = props.display) !== null && _f !== void 0 ? _f : Display.Flex);
    if (wrapper.props.flex !== props.flex)
        node.setFlex(props.flex);
    if (wrapper.props.flexDirection !== props.flexDirection)
        node.setFlexDirection((_g = props.flexDirection) !== null && _g !== void 0 ? _g : FlexDirection.Column);
    if (wrapper.props.flexBasis !== props.flexBasis)
        node.setFlexBasis(props.flexBasis);
    if (wrapper.props.flexGrow !== props.flexGrow)
        node.setFlexGrow(props.flexGrow);
    if (wrapper.props.flexShrink !== props.flexShrink)
        node.setFlexShrink(props.flexShrink);
    if (wrapper.props.flexWrap !== props.flexWrap)
        node.setFlexWrap((_h = props.flexWrap) !== null && _h !== void 0 ? _h : Wrap.NoWrap);
    if (wrapper.props.isReferenceBaseline !== props.isReferenceBaseline)
        node.setIsReferenceBaseline((_j = props.isReferenceBaseline) !== null && _j !== void 0 ? _j : false);
    if (wrapper.props.gap !== props.gap)
        node.setGap(Gutter.All, props.gap);
    if (wrapper.props.rowGap !== props.rowGap)
        node.setGap(Gutter.Row, props.rowGap);
    if (wrapper.props.columnGap !== props.columnGap)
        node.setGap(Gutter.Column, props.columnGap);
    if (wrapper.props.justifyContent !== props.justifyContent)
        node.setJustifyContent((_k = props.justifyContent) !== null && _k !== void 0 ? _k : Justify.FlexStart);
    if (wrapper.props.border !== props.border)
        node.setBorder(Edge.All, props.border);
    if (wrapper.props.borderTop !== props.borderTop)
        node.setBorder(Edge.Top, props.borderTop);
    if (wrapper.props.borderBottom !== props.borderBottom)
        node.setBorder(Edge.Bottom, props.borderBottom);
    if (wrapper.props.borderLeft !== props.borderLeft)
        node.setBorder(Edge.Left, props.borderLeft);
    if (wrapper.props.borderRight !== props.borderRight)
        node.setBorder(Edge.Right, props.borderRight);
    if (wrapper.props.margin !== props.margin)
        node.setMargin(Edge.All, props.margin);
    if (wrapper.props.marginTop !== props.marginTop)
        node.setMargin(Edge.Top, props.marginTop);
    if (wrapper.props.marginBottom !== props.marginBottom)
        node.setMargin(Edge.Bottom, props.marginBottom);
    if (wrapper.props.marginLeft !== props.marginLeft)
        node.setMargin(Edge.Left, props.marginLeft);
    if (wrapper.props.marginRight !== props.marginRight)
        node.setMargin(Edge.Right, props.marginRight);
    if (wrapper.props.maxHeight !== props.maxHeight)
        node.setMaxHeight(props.maxHeight);
    if (wrapper.props.maxWidth !== props.maxWidth)
        node.setMaxWidth(props.maxWidth);
    if (wrapper.props.minHeight !== props.minHeight)
        node.setMinHeight(props.minHeight);
    if (wrapper.props.minWidth !== props.minWidth)
        node.setMinWidth(props.minWidth);
    if (wrapper.props.overflow !== props.overflow)
        node.setOverflow((_l = props.overflow) !== null && _l !== void 0 ? _l : Overflow.Hidden);
    if (wrapper.props.padding !== props.padding)
        node.setPadding(Edge.All, props.padding);
    if (wrapper.props.paddingTop !== props.paddingTop)
        node.setPadding(Edge.Top, props.paddingTop);
    if (wrapper.props.paddingBottom !== props.paddingBottom)
        node.setPadding(Edge.Bottom, props.paddingBottom);
    if (wrapper.props.paddingLeft !== props.paddingLeft)
        node.setPadding(Edge.Left, props.paddingLeft);
    if (wrapper.props.paddingRight !== props.paddingRight)
        node.setPadding(Edge.Right, props.paddingRight);
    if (wrapper.props.position !== props.position)
        node.setPositionType((_m = props.position) !== null && _m !== void 0 ? _m : PositionType.Relative);
    if (wrapper.props.top !== props.top)
        node.setPosition(Edge.Top, props.top);
    if (wrapper.props.bottom !== props.bottom)
        node.setPosition(Edge.Bottom, props.bottom);
    if (wrapper.props.left !== props.left)
        node.setPosition(Edge.Left, props.left);
    if (wrapper.props.right !== props.right)
        node.setPosition(Edge.Right, props.right);
    wrapper.props = Object.assign({}, props);
}
/**
 * Host configuration object used by react-reconciler to map React operations
 * to Wonderland Engine scene graph operations.
 *
 * This object implements the host config callbacks required by the reconciler.
 * It is intentionally documented at a high level because individual callbacks
 * are already small and self-explanatory in the implementation.
 */
const HostConfig = {
    getRootHostContext(context) {
        return context;
    },
    getChildHostContext(parentHostContext) {
        return parentHostContext;
    },
    shouldSetTextContent(tag) {
        return false;
    },
    createTextInstance(text, ctx, hostContext, node) {
        debug('createTextInstance', text, ctx, hostContext, node);
    },
    createInstance(tag, props, ctx) {
        debug('createInstance', tag, props, ctx);
        const node = yoga.Node.create(ctx.config);
        const w = new NodeWrapper(ctx, node, tag);
        ctx.addNodeWrapper(w);
        if (tag === 'text3d') {
            node.setMeasureFunc((width, widthMode, height, heightMode) => {
                var _a, _b;
                const t = (_a = w.object) === null || _a === void 0 ? void 0 : _a.getComponent(TextComponent);
                if (t) {
                    const s = TEXT_BASE_SIZE *
                        ((_b = w.props.fontSize) !== null && _b !== void 0 ? _b : DEFAULT_FONT_SIZE) *
                        ctx.comp.scaling[1];
                    const ww = (width * ctx.comp.scaling[0]) / s;
                    t.wrapWidth = ww;
                    const bb = t.getBoundingBox();
                    const bbWidth = ((bb[2] - bb[0]) * s) / ctx.comp.scaling[0];
                    let bbHeight = ((bb[3] - bb[1]) * s) / ctx.comp.scaling[1];
                    const font = t.material.getFont();
                    if (font) {
                        let h = 0;
                        h = (font.emHeight * s) / ctx.comp.scaling[1];
                        if (h > bbHeight ||
                            t.wrapMode === TextWrapMode.None ||
                            t.wrapMode === TextWrapMode.Clip) {
                            bbHeight = h;
                        }
                    }
                    let calculatedWidth = 0;
                    if (widthMode === MeasureMode.Undefined) {
                        calculatedWidth = bbWidth;
                    }
                    else if (widthMode === MeasureMode.Exactly) {
                        calculatedWidth = width;
                    }
                    else if (widthMode === MeasureMode.AtMost) {
                        calculatedWidth = Math.min(bbWidth, width);
                    }
                    let calculatedHeight = 0;
                    if (heightMode === MeasureMode.Undefined) {
                        calculatedHeight = bbHeight;
                    }
                    else if (heightMode === MeasureMode.Exactly) {
                        calculatedHeight = height;
                    }
                    else if (heightMode === MeasureMode.AtMost) {
                        calculatedHeight = Math.min(bbHeight, height);
                    }
                    node.setHeight(calculatedHeight);
                    node.setWidth(calculatedWidth);
                    return { width: calculatedWidth, height: calculatedHeight };
                }
                else {
                    return { width: width, height: height };
                }
            });
        }
        applyToYogaNode(tag, node, props, w, ctx);
        return w;
    },
    appendInitialChild(parent, child) {
        debug('appendInitialChild', child, parent);
        if (!child)
            return;
        applyToYogaNode(child.tag, child.node, child.props, child);
        parent.node.insertChild(child.node, parent.node.getChildCount());
        child.parent = parent;
        parent.children.push(child);
        parent.ctx.comp.needsUpdate = true;
    },
    appendChild(parent, child) {
        debug('appendChild', parent, child);
        if (!child)
            return;
        applyToYogaNode(child.tag, child.node, child.props, child);
        parent.node.insertChild(child.node, parent.node.getChildCount());
        child.parent = parent;
        parent.children.push(child);
        parent.ctx.comp.needsUpdate = true;
    },
    appendChildToContainer(ctx, child) {
        debug('appendChildToContainer', ctx, child);
        if (!child)
            return;
        ctx.root = child;
        ctx.comp.needsUpdate = true;
    },
    insertInContainerBefore(container, child, beforeChild) {
        debug('insertContainerBefore', container, child, beforeChild);
        if (!child)
            return;
        // When inserting into the root container, set the root and mark for update
        container.root = child;
        container.comp.needsUpdate = true;
    },
    insertBefore(parent, child, before) {
        debug('insertBefore', parent, child, before);
        if (!child)
            return;
        applyToYogaNode(child.tag, child.node, child.props, child);
        const beforeIndex = parent.children.findIndex((childNode) => childNode === before);
        const insertIndex = beforeIndex !== -1 ? beforeIndex : parent.children.length;
        parent.node.insertChild(child.node, insertIndex);
        parent.children.splice(insertIndex, 0, child);
        child.parent = parent;
        parent.ctx.comp.needsUpdate = true;
    },
    removeChild(parent, child) {
        debug('removeChild', parent, child);
        if (!child)
            return;
        destroyTreeForNode(child, parent.ctx);
        parent.ctx.comp.needsUpdate = true;
    },
    removeChildFromContainer(ctx, child) {
        debug('removeChildFromContainer', ctx, child);
        if (!child)
            return;
        destroyTreeForNode(child, ctx);
    },
    finalizeInitialChildren(instance, tag, props, ctx, hostContext) {
        debug('finalizeInitialChildren', instance, tag);
        return false;
    },
    prepareForCommit(ctx) {
        debug('prepareForCommit');
        return null;
    },
    resetAfterCommit(containerInfo) {
        debug('resetAfterCommit', containerInfo);
    },
    commitUpdate(instance, updatePayload, type, prevProps, nextProps, internalHandle) {
        debug('commitUpdate');
        instance.props = nextProps;
        instance.dirty = true;
        applyToYogaNode(instance.tag, instance.node, instance.props, instance);
        instance.ctx.comp.needsUpdate = true;
    },
    prepareUpdate(instance, tag, oldProps, newProps, rootContainer, hostContext) {
        debug('prepareUpdate', oldProps, newProps);
        if (propsEqual(oldProps, newProps))
            return null;
        return {};
    },
    getPublicInstance(instance) {
        return instance.object;
    },
    afterActiveInstanceBlur() { },
    beforeActiveInstanceBlur() { },
    detachDeletedInstance(node) { },
    getCurrentEventPriority() {
        return 0;
    },
    getInstanceFromNode(node) {
        return null;
    },
    clearContainer(ctx) {
        debug('clearContainer', ctx);
        if (!ctx.root)
            return;
        destroyTreeForNode(ctx.root, ctx);
        ctx.root = null;
    },
    getInstanceFromScope(scopeInstance) {
        debug('getInstanceFromScope', scopeInstance);
        return null;
    },
    scheduleTimeout: setTimeout,
    cancelTimeout: clearTimeout,
    supportsMutation: true,
    supportsHydration: false,
    supportsPersistence: false,
    isPrimaryRenderer: false,
    noTimeout: -1,
    preparePortalMount(containerInfo) { },
    prepareScopeUpdate(scopeInstance, instance) { },
};
/**
 * The reconciler instance created from the HostConfig.
 *
 * This is the primary entrypoint used by the renderer implementation to create
 * React containers and drive updates into the Wonderland Engine scene.
 */
export const reconcilerInstance = Reconciler(HostConfig);
/**
 * Recursively destroy a NodeWrapper subtree and free underlying resources.
 *
 * This function assumes wrappers and their .node fields are valid and that the
 * caller can safely mutate the tree. It will null out parent/child links on
 * the destroyed wrapper.
 *
 * @param child - The root of the subtree to destroy.
 * @param ctx - The Context that owns this wrapper (used for cleanup).
 */
export function destroyTreeForNode(child, ctx) {
    var _a, _b;
    const childCount = (_a = child.children.length) !== null && _a !== void 0 ? _a : 0;
    for (let c = childCount - 1; c >= 0; --c) {
        destroyTreeForNode(child.children[c], ctx);
    }
    if (child.parent) {
        const parent = child.parent;
        parent.node.removeChild(child.node);
        parent.children.splice(parent.children.indexOf(child), 1);
        child.parent = null;
    }
    child.node.free();
    if (child.object && !child.object.isDestroyed) {
        child.object.destroy();
    }
    child.object = null;
    (_b = child.ctx) === null || _b === void 0 ? void 0 : _b.removeNodeWrapper(child);
}
/**
 * Small wrapper object that pairs a Yoga node with runtime metadata.
 *
 * NodeWrapper instances are lightweight containers used by the renderer to
 * maintain layout nodes, applied props, an optional rendered object reference,
 * and tree structure information (parent/children). They are stored in a
 * Context so they can be queried and cleaned up later.
 */
export class NodeWrapper {
    /**
     * Construct a NodeWrapper.
     *
     * @param ctx - Owning Context.
     * @param node - The Yoga node associated with this wrapper.
     * @param tag - A short debug tag for the node.
     */
    constructor(ctx, node, tag) {
        /** Applied properties cache (style/props applied to this node). */
        this.props = {};
        /** Optional runtime object produced by the renderer (e.g. scene object). */
        this.object = null;
        /** Parent wrapper in the tree, or null for the root. */
        this.parent = null;
        /** Ordered list of child wrappers. */
        this.children = [];
        /** Hover state for mouse/pointer handling (internal two-slot boolean). */
        this.hovering = [false, false];
        /** Dirty flag indicating that layout/props need to be re-applied. */
        this.dirty = true;
        this.ctx = ctx;
        this.tag = tag;
        this.node = node;
    }
}
/**
 * Context holds renderer-global state for a particular root tree.
 *
 * Each Context creates a Yoga Config (using the shared {@link yoga})
 * and maintains a registry of NodeWrapper instances created for that tree.
 * The Context is intentionally small: it provides registration helpers and a
 * lightweight tree-printing utility used for debugging.
 */
export class Context {
    constructor(c, yoga) {
        this.wrappers = [];
        this.root = null;
        this.comp = c;
        // The shared Yoga instance is set by the loader/initializer.
        // We assume it exists when a Context is constructed via initializeRenderer.
        this.config = yoga.Config.create();
        this.config.setUseWebDefaults(false);
        this.config.setPointScaleFactor(1);
    }
    /** Register a NodeWrapper with this Context. */
    addNodeWrapper(w) {
        this.wrappers.push(w);
        w.ctx = this;
    }
    /** Remove a NodeWrapper from this Context's registry. */
    removeNodeWrapper(w) {
        const i = this.wrappers.indexOf(w);
        this.wrappers.splice(i, 1);
    }
    /**
     * Print the tree to the console for lightweight debugging.
     *
     * The output shows the node tag, computed layout rectangle, and the props
     * cache. The format is intentionally minimal; host modules may provide
     * richer debugging facilities.
     *
     * @param node - Optional starting node (defaults to the Context root).
     * @param prefix - Optional string prefix used for indentation when
     * printing recursively.
     */
    printTree(node, prefix) {
        node = node !== null && node !== void 0 ? node : this.root;
        prefix = prefix !== null && prefix !== void 0 ? prefix : '';
        const yn = node.node;
        // Keep printing lightweight; real debug is provided by the host
        // module that imports this core.
        // eslint-disable-next-line no-console
        console.log(prefix + node.tag, `{${yn.getComputedLeft()}, ${yn.getComputedTop()}, ${yn.getComputedWidth()}, ${yn.getComputedHeight()}}`, node.props);
        if (!node.children)
            return;
        for (let n of node.children) {
            this.printTree(n, prefix + '--');
        }
    }
    computeUIBounds() {
        if (!this.root)
            return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
        const rootLeft = this.root.node.getComputedLeft();
        const rootTop = this.root.node.getComputedTop();
        const rootWidth = this.root.node.getComputedWidth();
        const rootHeight = this.root.node.getComputedHeight();
        let minX = rootLeft, maxX = rootLeft + rootWidth, minY = rootTop, maxY = rootTop + rootHeight;
        const traverse = (node) => {
            var _a;
            const left = node.node.getComputedLeft();
            const top = node.node.getComputedTop();
            const width = node.node.getComputedWidth();
            const height = node.node.getComputedHeight();
            if (isNaN(left) || isNaN(top) || isNaN(width) || isNaN(height)) {
                const invalids = [];
                if (isNaN(left))
                    invalids.push(`left=${left}`);
                if (isNaN(top))
                    invalids.push(`top=${top}`);
                if (isNaN(width))
                    invalids.push(`width=${width}`);
                if (isNaN(height))
                    invalids.push(`height=${height}`);
                throw new Error(`Context.computeUIBounds: Invalid layout values detected for node tag="${(_a = node.tag) !== null && _a !== void 0 ? _a : 'unknown'}"` +
                    ` [${invalids.join(', ')}]`);
            }
            minX = Math.min(minX, left);
            maxX = Math.max(maxX, left + width);
            minY = Math.min(minY, top);
            maxY = Math.max(maxY, top + height);
            node.children.forEach(traverse);
        };
        traverse(this.root);
        return { minX, maxX, minY, maxY };
    }
}
export class ReactUiBase extends Component {
    constructor() {
        super(...arguments);
        this.space = 0;
        this.width = 100;
        this.height = 100;
        this.scalingMode = ScalingType.Absolute;
        this.manualHeight = 1080;
        this.manualWidth = 1080;
        /** Collision group for the ray cast. Only objects in this group will be affected by this cursor. */
        this.collisionGroup = 0xff;
        /** Mode for raycasting, whether to use PhysX or simple collision components. Auto uses PhysX if enabled in the project */
        this.rayCastMode = RayCastMode.Auto;
        /**
         * Device pixel ratio, defaults to 1. Used on mobile/tablet devices to scale.
         */
        this.dpr = 1;
        this._onViewportResize = () => {
            /* This callback is only added if space is "screen" */
            const activeView = this.engine.scene.activeViews[0];
            if (!activeView)
                return;
            /* Projection matrix will change if the viewport is resized, which will affect the
             * projection matrix because of the aspect ratio. */
            mat4.invert(invProj, activeView.projectionMatrix);
            vec3.transformMat4(topLeft, [-1, 1, 0], invProj);
            vec3.transformMat4(bottomRight, [1, -1, 0], invProj);
            const s = bottomRight[0] - topLeft[0];
            this.object.setScalingLocal([s, s, s]);
            /* Convert from yoga units to 0-1 */
            this.dpr = window.devicePixelRatio;
            this.width = this._dpiAdjust(this.engine.canvas.clientWidth);
            this.height = this._dpiAdjust(this.engine.canvas.clientHeight);
            this.scaling = [1 / this.width, 1 / this.width];
            if (activeView.projectionType == ProjectionType.Orthographic) {
                this.object.setPositionLocal([topLeft[0], topLeft[1], -1]);
            }
            else {
                this.object.setPositionLocal(topLeft);
            }
            this.needsUpdate = true;
            this.viewportChanged = true;
        };
        this.scaling = [0.01, 0.01];
        this.ctx = null;
        this.needsUpdate = true;
        this.viewportChanged = true;
        this.callbacks = {};
        /** 'pointermove' event listener */
        this.curGen = 0;
        this.onMove = ({ x, y, e }) => {
            if (!this.ctx)
                return null;
            const cur = (this.curGen = this.curGen ^ 0x1);
            /* Clear hovering flag */
            this.ctx.wrappers.forEach((w) => (w.hovering[cur] = false));
            const target = this.emitEvent('onMove', x, y, e);
            this.updateHoverState(x, y, e, target);
        };
        /** 'click' event listener */
        this.onClick = (e) => {
            const t = this.emitEvent('onClick', e.x, e.y, e.e);
            return t;
        };
        this.onDown = (e) => {
            const t = this.emitEvent('onDown', e.x, e.y, e.e);
            return t;
        };
        this.onUp = (e) => {
            const t = this.emitEvent('onUp', e.x, e.y, e.e);
            return t;
        };
    }
    get pixelSizeAdjustment() {
        switch (this.scalingMode) {
            case ScalingType.FixedHeight:
                return this.manualHeight / this.engine.canvas.height;
            case ScalingType.FixedHeightLimitedWidth:
                const factor = this.engine.canvas.height / this.manualHeight;
                if (this.engine.canvas.width / factor < this.manualWidth) {
                    return this.manualWidth / this.engine.canvas.width;
                }
                else {
                    return this.manualHeight / this.engine.canvas.height;
                }
            default:
                return 1;
        }
    }
    static onRegister(engine) {
        engine.registerComponent(CursorTarget);
    }
    setContext(c) {
        this.ctx = c;
    }
    updateLayout() {
        var _a, _b, _c;
        if (!((_a = this.ctx) === null || _a === void 0 ? void 0 : _a.root))
            return;
        debug('updateLayout', this.width, this.height);
        this.ctx.wrappers.forEach((w) => applyToYogaNode(w.tag, w.node, w.props, w));
        this.ctx.root.node.calculateLayout((_b = this.width) !== null && _b !== void 0 ? _b : 100, (_c = this.height) !== null && _c !== void 0 ? _c : 100, Direction.LTR);
        applyLayoutToSceneGraph(this.ctx.root, this.ctx, this.viewportChanged);
        if (this.space === UISpace.World && this._colliderObject) {
            const bounds = this.ctx.computeUIBounds();
            const width = bounds.maxX - bounds.minX;
            const height = bounds.maxY - bounds.minY;
            const centerX = (bounds.minX + bounds.maxX) / 2;
            const centerY = (bounds.minY + bounds.maxY) / 2;
            const rootScaling = this.object.getScalingWorld(tempScale);
            // Adjust for scaling
            const scaledWidth = width * this.scaling[0];
            const scaledHeight = height * this.scaling[1];
            const scaledCenterX = centerX * this.scaling[0];
            const scaledCenterY = -centerY * this.scaling[1]; // Flip Y for Wonderland coords
            if (this._usePhysx()) {
                this._colliderObject.active = false;
            }
            // Update extents (half-size)
            const extents = new Float32Array(3);
            extents[0] = 0.5 * scaledWidth * rootScaling[0]; // Half-width, scaled
            extents[1] = 0.5 * scaledHeight * rootScaling[1]; // Half-height, scaled
            extents[2] = COLLIDER_THICKNESS / 2; // Keep fixed depth
            // Update collider position (center of bounds)
            this._colliderObject.setPositionLocal([
                scaledCenterX,
                scaledCenterY,
                COLLIDER_THICKNESS / 2,
            ]);
            if (this._usePhysx()) {
                const physx = this._colliderObject.getComponent(PhysXComponent);
                physx.extents = extents;
                this._colliderObject.active = true;
            }
            else {
                const collision = this._colliderObject.getComponent(CollisionComponent);
                collision.extents.set(extents);
            }
        }
        this.needsUpdate = false;
    }
    init() {
        /* We need to ensure React defers re-renders to after the callbacks were called */
        const onMove = this.onMove;
        this.onMove = (e) => reconcilerInstance.batchedUpdates(onMove, e);
        const onClick = this.onClick;
        this.onClick = (e) => reconcilerInstance.batchedUpdates(onClick, e);
        const onUp = this.onUp;
        this.onUp = (e) => reconcilerInstance.batchedUpdates(onUp, e);
        const onDown = this.onDown;
        this.onDown = (e) => reconcilerInstance.batchedUpdates(onDown, e);
        this.callbacks = {
            click: this.onPointerClick.bind(this),
            pointermove: this.onPointerMove.bind(this),
            pointerdown: this.onPointerDown.bind(this),
            pointerup: this.onPointerUp.bind(this),
        };
    }
    async start() {
        if (this.space == UISpace.Screen) {
            this._viewComponent = this.engine.scene.activeViews[0];
            /* Reparent to main view */
            this.object.parent = this._viewComponent.object;
            this.object.setPositionLocal([0, 0, -2 * this._viewComponent.near]);
            this.object.resetRotation();
            /* Calculate size of the UI */
            this._onViewportResize();
        }
        this.renderer = await initializeRenderer();
        this.renderer.render(this.render(), this);
    }
    update(dt = undefined) {
        if (this.needsUpdate) {
            this.updateLayout();
        }
    }
    getCursorPosition(c) {
        this.object.transformPointInverseWorld(tempPos, c.cursorPos);
        this.object.getScalingWorld(tempScale);
        return [
            tempPos[0] / this.scaling[0] / tempScale[0],
            -tempPos[1] / this.scaling[1] / tempScale[1],
        ];
    }
    onActivate() {
        var _a;
        if (this.space == UISpace.World) {
            this._colliderObject =
                (_a = this.object.findByNameDirect('UIColliderObject')[0]) !== null && _a !== void 0 ? _a : (() => {
                    const o = this.engine.scene.addObject(this.object);
                    o.name = 'UIColliderObject';
                    o.addComponent(CursorTarget);
                    if (this._usePhysx()) {
                        o.addComponent(PhysXComponent, {
                            shape: Shape.Box,
                            static: true,
                            group: this.collisionGroup,
                        });
                    }
                    else {
                        o.addComponent(CollisionComponent, {
                            collider: Collider.Box,
                            group: this.collisionGroup,
                        });
                    }
                    return o;
                })();
            const target = this._colliderObject.getComponent(CursorTarget);
            target.onClick.add((_, c, e) => {
                const [x, y] = this.getCursorPosition(c);
                this.onClick({ x, y, e: e });
            }, { id: 'onClick' });
            target.onMove.add((_, c, e) => {
                const [x, y] = this.getCursorPosition(c);
                this.onMove({ x, y, e });
            }, { id: 'onMove' });
            target.onUp.add((_, c, e) => {
                const [x, y] = this.getCursorPosition(c);
                this.onUp({ x, y, e: e });
            }, { id: 'onUp' });
            target.onDown.add((_, c, e) => {
                const [x, y] = this.getCursorPosition(c);
                this.onDown({ x, y, e: e });
            }, { id: 'onDown' });
            const extents = this.object.getScalingWorld(new Float32Array(3));
            extents[0] *= 0.5 * this.width * this.scaling[0];
            extents[1] *= 0.5 * this.height * this.scaling[1];
            extents[2] = COLLIDER_THICKNESS / 2; // Keep fixed depth
            if (this._usePhysx()) {
                this._colliderObject.active = false;
                const physx = this._colliderObject.getComponent(PhysXComponent);
                physx.extents = extents;
                this._colliderObject.setPositionLocal([
                    this.width * 0.5 * this.scaling[0],
                    -this.height * 0.5 * this.scaling[1],
                    COLLIDER_THICKNESS / 2,
                ]);
                this._colliderObject.active = true;
            }
            else {
                const collision = this._colliderObject.getComponent(CollisionComponent);
                collision.extents.set(extents);
                this._colliderObject.setPositionLocal([
                    this.width * 0.5 * this.scaling[0],
                    -this.height * 0.5 * this.scaling[1],
                    COLLIDER_THICKNESS / 2,
                ]);
            }
        }
        else {
            this.engine.onResize.add(this._onViewportResize);
            for (const [k, v] of Object.entries(this.callbacks)) {
                // Use capture:true to intercept events before they bubble,
                // ensuring UI events are handled before other handlers.
                // This allows them to be stopped from propagating further if needed.
                this.engine.canvas.addEventListener(k, v, { capture: true });
            }
        }
    }
    onDeactivate() {
        if (this.space == UISpace.World) {
            if (!this._colliderObject)
                return;
            const target = this._colliderObject.getComponent(CursorTarget);
            if (!target)
                return;
            // FIXME: We might be able to just deactivate the target here instead?
            target.onClick.remove('onClick');
            target.onMove.remove('onMove');
            target.onUp.remove('onUp');
            target.onDown.remove('onDown');
        }
        else {
            this.engine.onResize.remove(this._onViewportResize);
            if (!this._viewComponent)
                return;
            const canvas = this.engine.canvas;
            for (const [k, v] of Object.entries(this.callbacks)) {
                canvas.removeEventListener(k, v);
            }
        }
    }
    onDestroy() {
        var _a;
        (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.unmountRoot();
    }
    forEachElementUnderneath(node, x, y, callback) {
        var _a;
        if (node === null)
            return null;
        const t = node.node.getComputedTop();
        const l = node.node.getComputedLeft();
        const w = node.node.getComputedWidth();
        const h = node.node.getComputedHeight();
        const inside = !(x > l + w || x < l || y > t + h || y < t);
        let target = null;
        if (inside) {
            node.hovering[this.curGen] = true;
            if (callback(node))
                target = node;
        }
        for (let n of node.children) {
            target = (_a = this.forEachElementUnderneath(n, x - l, y - t, callback)) !== null && _a !== void 0 ? _a : target;
        }
        return target;
    }
    emitEvent(eventName, x, y, e) {
        var _a;
        if (!((_a = this.ctx) === null || _a === void 0 ? void 0 : _a.root))
            return null;
        const target = this.forEachElementUnderneath(this.ctx.root, x, y, (w) => {
            const event = w === null || w === void 0 ? void 0 : w.props[eventName];
            if (event !== undefined) {
                event({ x, y, e });
                return true;
            }
            return false;
        });
        if (DEBUG_EVENTS) {
            debug(eventName, `{${x}, ${y}}`, target);
            this.ctx.printTree();
        }
        return target;
    }
    onPointerMove(e) {
        /* Don't care about secondary pointers */
        if (!e.isPrimary)
            return null;
        const x = this._dpiAdjust(e.clientX);
        const y = this._dpiAdjust(e.clientY);
        this.onMove({ x, y, e });
    }
    updateHoverState(x, y, e, node) {
        const cur = this.curGen;
        const other = cur ^ 0x1;
        while (node) {
            node.hovering[cur] = true;
            node = node.parent;
        }
        this.ctx.wrappers.forEach((w) => {
            const hovering = w.hovering[cur];
            if (hovering != w.hovering[other]) {
                const event = hovering ? w === null || w === void 0 ? void 0 : w.props.onHover : w === null || w === void 0 ? void 0 : w.props.onUnhover;
                if (event !== undefined) {
                    event({ x, y, e });
                    return true;
                }
            }
        });
    }
    onPointerClick(e) {
        const x = this._dpiAdjust(e.clientX);
        const y = this._dpiAdjust(e.clientY);
        this.onClick({ x, y, e });
    }
    /** 'pointerdown' event listener */
    onPointerDown(e) {
        /* Don't care about secondary pointers or non-left clicks */
        if (!e.isPrimary || e.button !== 0)
            return null;
        const x = this._dpiAdjust(e.clientX);
        const y = this._dpiAdjust(e.clientY);
        return this.onDown({ x, y, e });
    }
    /** 'pointerup' event listener */
    onPointerUp(e) {
        /* Don't care about secondary pointers or non-left clicks */
        if (!e.isPrimary || e.button !== 0)
            return null;
        const x = this._dpiAdjust(e.clientX);
        const y = this._dpiAdjust(e.clientY);
        return this.onUp({ x, y, e });
    }
    renderCallback() {
        // FIXME: Never called
        this.needsUpdate = true;
    }
    _dpiAdjust(value) {
        return value * this.pixelSizeAdjustment * this.dpr;
    }
    _usePhysx() {
        if (this.rayCastMode === RayCastMode.AlwaysPhysX ||
            (this.rayCastMode === RayCastMode.Auto && this.engine.physics))
            return true;
        return false;
    }
}
ReactUiBase.TypeName = 'react-ui-base';
__decorate([
    property.enum(['world', 'screen'])
], ReactUiBase.prototype, "space", void 0);
__decorate([
    property.material({ required: true })
], ReactUiBase.prototype, "textMaterial", void 0);
__decorate([
    property.material({ required: true })
], ReactUiBase.prototype, "panelMaterial", void 0);
__decorate([
    property.material({ required: true })
], ReactUiBase.prototype, "panelMaterialTextured", void 0);
__decorate([
    property.int(100)
], ReactUiBase.prototype, "width", void 0);
__decorate([
    property.int(100)
], ReactUiBase.prototype, "height", void 0);
__decorate([
    property.enum(Object.keys(ScalingType).filter((e) => isNaN(Number(e))), ScalingType.Absolute)
], ReactUiBase.prototype, "scalingMode", void 0);
__decorate([
    property.float(1080)
], ReactUiBase.prototype, "manualHeight", void 0);
__decorate([
    property.float(1080)
], ReactUiBase.prototype, "manualWidth", void 0);
__decorate([
    property.int(0xff)
], ReactUiBase.prototype, "collisionGroup", void 0);
__decorate([
    property.enum(['auto', 'collision', 'physx'], 'auto')
], ReactUiBase.prototype, "rayCastMode", void 0);
/** The Yoga API instance used to create and configure layouts.
 *
 */
let yoga = null;
let yogaInitializationPromise = null;
export async function initializeRenderer() {
    if (!yoga) {
        if (!yogaInitializationPromise) {
            yogaInitializationPromise = loadYoga().then((loadedYoga) => {
                yoga = loadedYoga;
            });
        }
        await yogaInitializationPromise;
    }
    return {
        rootContainer: null,
        unmountRoot() {
            reconcilerInstance.updateContainer(null, this.rootContainer);
        },
        render(element, reactComp, callback) {
            const container = reconcilerInstance.createContainer(new Context(reactComp, yoga), 0, null, false, null, 'root', (e) => console.error(e), null);
            this.rootContainer = container;
            reactComp.setContext(container.containerInfo);
            const parentComponent = null;
            reconcilerInstance.updateContainer(element, container, parentComponent, reactComp.renderCallback.bind(reactComp));
            reconcilerInstance.injectIntoDevTools({
                bundleType: 0,
                version: '0.2.1',
                rendererPackageName: 'wonderlandengine/react-ui',
            });
        },
    };
}
